import { buildDirectoryUrl } from './slugs';
import { buildStateHubUrl, DEFAULT_STATE, stateSlugToDisplay } from './stateCore.mjs';
import type { StateSlug } from './stateCore.mjs';

/**
 * THE BREADCRUMB SPINE (owner decision 2026-07-25) — one hierarchy site-wide:
 *
 *   Tier 1  Home           /
 *   Tier 2  {NSW} Suburbs  /[state]/suburbs     ← the single state node (UI-57: was /[state]/gyms)
 *   Tier 3  {Suburb}       /[state]/[suburb]/gyms
 *   Tier 4  {Gym Name}     leaf (self URL in JSON-LD, plain text when visible)
 *
 * Policies this file encodes:
 *  - P1 the '/gyms' chooser and the /[state]/gyms search directories are NOT
 *       tiers — they are sibling utilities, not URL ancestors of the suburb
 *       pages, so they appear in no trail (UI-57 widened this from one page to
 *       three; the principle is unchanged).
 *  - P2 the state node has ONE label + ONE href everywhere, derived here
 *       (audit #2/#15/#19: the label was 'NSW' on gym pages vs 'NSW Suburbs' on
 *       the hub, and its href was /[state]/sydney — a URL that serves the
 *       not-found shell, i.e. a soft-404 baked into gym structured data).
 *  - P3 every trail starts at Home (audit #18: the gym trail had no root).
 *  - P4 the CURRENT page's crumb carries `selfUrl`, not `href`: emitted as the
 *       JSON-LD `item` but never rendered as a link.
 *
 * The blog trail (Home › Blog › Category › Post) is a sibling branch off the
 * same tier-1 root — see generateBlogBreadcrumbs below, used by BOTH
 * pages/BlogPostPage.tsx (visible) and the SSG (JSON-LD).
 *
 * WHERE THE JSON-LD IS EMITTED (P6, 2026-07-25 — exactly ONE emitter per page):
 *  - gym profile   SSG only, as a second <script> beside the gym's
 *                  LocalBusiness/SportsActivityLocation block
 *                  (vite.config.ts, routeInfo.type === 'gym')
 *  - blog post     SSG only, beside the Article block
 *  - /[state]/suburbs (the hub; UI-57: was /[state]/gyms) SSG only, via lib/ssgSeoAssembly buildHubBreadcrumbJsonLd
 *  - directory/style pages  no breadcrumb at all (audit #17, intentional)
 *
 * DO NOT re-add a runtime Helmet BreadcrumbList to any of these pages. The gym
 * page's runtime copy was deleted when the SSG copy landed: two emitters put two
 * BreadcrumbLists in the hydrated DOM, the same duplicate-structured-data failure
 * that produced GSC "Review has multiple aggregate ratings" for the gym block.
 * Nothing in CI enforces this — no test or deploy script inspects JSON-LD — so
 * this note is the guardrail.
 */
interface BreadcrumbItem {
    label: string;
    /** Link target for the visible <Breadcrumb>. Absent → rendered as the current-page span. */
    href?: string;
    /**
     * The current page's own URL (P4). Consumed ONLY by generateBreadcrumbJsonLd,
     * which emits it as the ListItem `item`; the visible component ignores it and
     * renders plain text, so the trail never links to the page you are on.
     */
    selfUrl?: string;
}

const BASE_URL = 'https://martialartnetwork.com.au';

/** Tier 1 — shared root of every trail (P3). */
export function homeCrumb(): BreadcrumbItem {
    return { label: 'Home', href: '/' };
}

/** The state node's single label: 'nsw' → 'NSW Suburbs' (P2). */
export function stateSuburbsLabel(state: StateSlug = DEFAULT_STATE): string {
    return `${stateSlugToDisplay(state)} Suburbs`;
}

/** Tier 2 — the state node as an ANCESTOR (linked). */
export function stateSuburbsCrumb(state: StateSlug = DEFAULT_STATE): BreadcrumbItem {
    return { label: stateSuburbsLabel(state), href: buildStateHubUrl(state) };
}

/**
 * Trail for the suburbs hub page (/[state]/suburbs since UI-57) — visible + JSON-LD.
 * The state node IS this page, so it is the leaf: same label as tier 2
 * everywhere else, self-referencing in JSON-LD, plain text on screen (P4).
 */
export function generateHubBreadcrumbs(state: StateSlug = DEFAULT_STATE): BreadcrumbItem[] {
    return [
        homeCrumb(),
        { label: stateSuburbsLabel(state), selfUrl: buildStateHubUrl(state) },
    ];
}

/**
 * Trail for GymProfilePage: Home › [State] Suburbs › [Suburb] › [Gym Name].
 *
 * @param selfPath - the gym's own canonical path. Supplied → the leaf carries a
 *                   JSON-LD self URL (P4); omitted → the leaf has no `item`.
 */
export function generateGymBreadcrumbs(
    gymName: string,
    location: string,
    state: StateSlug = DEFAULT_STATE,
    selfPath?: string
): BreadcrumbItem[] {
    // exactOptionalPropertyTypes: build the leaf without the key rather than
    // assigning undefined to it.
    const leaf: BreadcrumbItem = selfPath
        ? { label: gymName, selfUrl: selfPath }
        : { label: gymName };

    return [
        homeCrumb(),
        stateSuburbsCrumb(state),
        { label: location, href: buildDirectoryUrl(location, state) },
        leaf,
    ];
}

/** Tier 2 of the blog branch — the blog index, as an ancestor. */
function blogCrumb(): BreadcrumbItem {
    return { label: 'Blog', href: '/blog' };
}

/**
 * Trail for the blog index itself: Home › Blog (Blog is the leaf, so selfUrl).
 * Blog list pages had NO trail at all while blog POSTS and the state hub did.
 */
export function generateBlogIndexBreadcrumbs(): BreadcrumbItem[] {
    return [homeCrumb(), { label: 'Blog', selfUrl: '/blog' }];
}

/**
 * Trail for a blog category page: Home › Blog › [Category].
 * The category is the leaf here, so it self-references rather than linking.
 */
export function generateBlogCategoryBreadcrumbs(categorySlug: string, categoryLabel: string): BreadcrumbItem[] {
    return [homeCrumb(), blogCrumb(), { label: categoryLabel, selfUrl: `/blog/${categorySlug}` }];
}

/**
 * Trail for a blog post: Home › Blog › [Category] › [Post Title].
 *
 * The sibling branch off the same tier-1 root. Labels are passed in rather than
 * imported so this core module does not reach into src/constants — both callers
 * (BlogPostPage's visible trail and the SSG's JSON-LD) already hold them, and
 * passing the SAME label from both is what keeps visible and structured data
 * identical.
 *
 * @param selfPath - the post's own canonical path; omitted → leaf has no `item`.
 */
export function generateBlogBreadcrumbs(
    categorySlug: string,
    categoryLabel: string,
    postLabel: string,
    selfPath?: string
): BreadcrumbItem[] {
    const leaf: BreadcrumbItem = selfPath
        ? { label: postLabel, selfUrl: selfPath }
        : { label: postLabel };

    return [
        homeCrumb(),
        blogCrumb(),
        { label: categoryLabel, href: `/blog/${categorySlug}` },
        leaf,
    ];
}

/**
 * Generate JSON-LD BreadcrumbList structured data.
 *
 * `siteUrl` is a parameter so the SSG can pass its own SITE_URL while runtime
 * callers keep the production default — one builder, both paths (audit #11).
 */
export function generateBreadcrumbJsonLd(items: BreadcrumbItem[], siteUrl: string = BASE_URL): object {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => {
            const listItem: { '@type': string; position: number; name: string; item?: string } = {
                '@type': 'ListItem',
                position: index + 1,
                name: item.label,
            };
            // An ancestor's href or the current page's selfUrl — both become `item`.
            const url = item.href ?? item.selfUrl;
            // '/' would produce `${siteUrl}/` — but the homepage's own canonical and
            // og:url are the NO-slash form, so emitting the slash stated the homepage
            // two ways across 1513 pages. Normalise to the canonical form. (href stays
            // '/' because the visible <Link> needs a real path.)
            if (url) listItem.item = url === '/' ? siteUrl : `${siteUrl}${url}`;
            return listItem;
        })
    };
}

export type { BreadcrumbItem };
